import { IconButton, Stack, Tooltip } from "@mui/material";
import {
  OrderDrawerIdentifier,
  useOrderDrawer,
} from "@features/orders-features/components/order-drawer";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReturnIcon from "@features/orders-features/components/rma/ReturnIcon";
import type { OrderRow } from "@features/orders-features/types/types";
import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppendIfPrefixExists } from "../../../../../../../modules/sitePaths";
import { OrderLineItemDto } from "@lib/ShiOneClient";
import { useClaims } from "../../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../../auth/authorizationTypes";
import { BuyAgainButton } from "@features/orders-features/components/BuyAgainButton";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useOrderTelemetrySection } from "../orderTelemetrySection";

export function OrderDrawerHeaderActions({
  id,
  order,
  canReturn,
  buyableItems,
  showOpenDetailsPageAction = true,
}: {
  id: OrderDrawerIdentifier;
  /** Loaded order; the return request dialog is opened with it. */
  order?: OrderRow | null;
  canReturn?: boolean;
  buyableItems: OrderLineItemDto[];
  showOpenDetailsPageAction?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    closeOrderDrawer,
    openReturnRequest,
    basePath: drawerBasePath,
  } = useOrderDrawer();
  const { hasPermission } = useClaims();
  const telemetrySection = useOrderTelemetrySection();

  const shoppingCartSave = hasPermission(ShiOnePermission.shoppingCartSave);
  const canBuyAgain = shoppingCartSave && !!buyableItems.length;

  // Web orders that lack a biOrderId are identified by their webOrderId (which is
  // also surfaced as the sales order number), so fall back to it for the route.
  const orderPathSegment = id.salesOrder?.trim()
    ? id.salesOrder.trim()
    : id.webOrderId != null
      ? String(id.webOrderId)
      : id.biOrderId != null
        ? String(id.biOrderId)
        : "";

  const canOpenOrderDetailsPage = orderPathSegment.length > 0;

  const orderDetailsPageHref = useCallback(() => {
    const params = new URLSearchParams();
    if (id.biOrderId != null) params.set("biOrderId", String(id.biOrderId));
    if (id.webOrderId != null) params.set("webOrderId", String(id.webOrderId));
    if (id.orgUnitId != null) params.set("orgUnitId", String(id.orgUnitId));
    const query = params.toString();
    const path = `/orders/details/${encodeURIComponent(orderPathSegment)}${
      query ? `?${query}` : ""
    }`;
    return AppendIfPrefixExists(path);
  }, [id.biOrderId, id.webOrderId, id.orgUnitId, orderPathSegment]);

  const onOpenOrderDetailsPage = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (drawerBasePath !== undefined) {
        // Replace the drawer entry so Back from the details page lands on the list, not the
        // drawer; closing first would add a second history change.
        navigate(orderDetailsPageHref(), {
          replace: true,
          state: { listUrl: `${drawerBasePath}${location.search}` },
        });
        return;
      }
      closeOrderDrawer();
      navigate(orderDetailsPageHref(), {
        state: { listUrl: `${location.pathname}${location.search}` },
      });
    },
    [
      closeOrderDrawer,
      drawerBasePath,
      navigate,
      orderDetailsPageHref,
      location,
    ],
  );

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        alignContent="flex-end"
      >
        {canReturn && order?.biOrderId && (
          <Tooltip title="Return/RMA">
            <IconButton
              size="small"
              aria-label="Return order"
              onClick={(e) => {
                e.stopPropagation();
                openReturnRequest(order);
              }}
              {...telemetryProps({
                name: "orders_return_rma",
                section: telemetrySection,
                label: "Return/RMA",
                ...(id.salesOrder ? { id: id.salesOrder } : {}),
              })}
            >
              <ReturnIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}
        {canBuyAgain && (
          <BuyAgainButton
            items={buyableItems}
            telemetrySection={telemetrySection}
            telemetryId={id.salesOrder ?? undefined}
          />
        )}
        {canOpenOrderDetailsPage && (
          <Tooltip title="Copy link to order details page">
            <IconButton
              size="small"
              aria-label="Copy link to order details page"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(
                  `${window.location.origin}${orderDetailsPageHref()}`,
                );
              }}
              {...telemetryProps({
                name: "orders_copy_link",
                section: telemetrySection,
                label: "Copy link to order details page",
                ...(id.salesOrder ? { id: id.salesOrder } : {}),
              })}
            >
              <LinkOutlinedIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}
        {showOpenDetailsPageAction && canOpenOrderDetailsPage && (
          <Tooltip title="Open order details page">
            <IconButton
              size="small"
              aria-label="Open order details page"
              onClick={onOpenOrderDetailsPage}
              {...telemetryProps({
                name: "orders_open_details",
                section: telemetrySection,
                label: "Open order details page",
                ...(id.salesOrder ? { id: id.salesOrder } : {}),
              })}
            >
              <OpenInNewIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </>
  );
}
